import { Clock, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import type { ProposalBatch } from "@stockline/shared/types/ai/proposal";

export interface DisplayField {
  label: string;
  value: string;
  oldValue?: string;
  payloadKey?: string;
}

export interface ProposalCardInnerProps {
  readonly batch: ProposalBatch;
  readonly showTimestamp?: boolean;
  readonly acting: boolean;
  readonly canAct: boolean;
  readonly onApprove: () => void;
  readonly onReject: () => void;
}

export const STATUS_STYLES: Record<
  string,
  {
    key: string;
    variant: "default" | "secondary" | "destructive" | "outline";
    icon: typeof Clock;
    className: string;
  }
> = {
  pending: {
    key: "pending",
    variant: "outline",
    icon: Clock,
    className:
      "bg-violet-50 border-violet-200 dark:bg-violet-900/20 dark:border-violet-800",
  },
  approved: {
    key: "approved",
    variant: "outline",
    icon: CheckCircle2,
    className:
      "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800",
  },
  rejected: {
    key: "rejected",
    variant: "outline",
    icon: XCircle,
    className: "bg-muted/30 border-border",
  },
  failed: {
    key: "failed",
    variant: "destructive",
    icon: AlertTriangle,
    className:
      "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800",
  },
};

export function translateTitle(
  title: string,
  t: (key: string) => string,
): string {
  if (title.startsWith("proposal.title.")) {
    const key = title.replace("proposal.title.", "");
    const translated = t(`aiProposals.proposalTitle.${key}`);
    if (translated !== `aiProposals.proposalTitle.${key}`) return translated;
  }
  return title;
}

export function translateFieldLabel(
  label: string,
  t: (key: string) => string,
): string {
  if (label.startsWith("line_")) return `#${label.replaceAll("line_", "")}`;
  if (label === "more_lines") return "...";
  const translated = t(`aiProposals.field.${label}`);
  if (translated === `aiProposals.field.${label}`)
    return label
      .replaceAll("_", " ")
      .replaceAll(/\b\w/g, (c) => c.toUpperCase());
  return translated;
}

export function translateFlowType(
  value: string,
  t: (key: string) => string,
): string {
  const [type, direction] = value.split(":");
  const typeT = t(`aiProposals.flowType.${type}`);
  const dirT = t(`aiProposals.flowDirection.${direction}`);
  const typePart = typeT === `aiProposals.flowType.${type}` ? type : typeT;
  const dirPart =
    dirT === `aiProposals.flowDirection.${direction}` ? direction : dirT;
  return `${typePart} (${dirPart})`;
}

const SKIP_PAYLOAD_KEYS = new Set([
  "display_fields",
  "metadata",
  "source_documents",
  "sourceDocuments",
  "message_ids",
  "delineation",
]);

export function isDisplayableEntry(
  key: string,
  value: unknown,
): value is string | number | boolean {
  if (SKIP_PAYLOAD_KEYS.has(key)) return false;
  if (key.endsWith("_id") || key.endsWith("Id")) return false;
  if (value === null || value === undefined) return false;
  return typeof value !== "object";
}

export function payloadToFields(
  payload: Record<string, unknown>,
): DisplayField[] {
  return Object.entries(payload)
    .filter(([k, v]) => isDisplayableEntry(k, v))
    .map(([key, value]) => ({
      label: key
        .replaceAll("_", " ")
        .replaceAll(/\b\w/g, (c) => c.toUpperCase()),
      value: typeof value === "string" ? value : String(value),
      payloadKey: key,
    }));
}

export function getDisplayFields(
  batch: ProposalBatch,
  t: (key: string, opts?: Record<string, string>) => string,
): DisplayField[] {
  for (const p of batch.proposals) {
    const fields = p.payload?.display_fields;
    if (Array.isArray(fields) && fields.length > 0) {
      return (fields as DisplayField[]).map((f) => {
        const label = translateFieldLabel(f.label, t);
        let value = f.value;
        if (f.label === "type" && value.includes(":")) {
          value = translateFlowType(value, t);
        }
        if (f.label === "more_lines") {
          value = t("aiProposals.field.more_lines", { count: f.value });
        }
        return { label, value, oldValue: f.oldValue, payloadKey: f.label };
      });
    }
  }
  return [];
}

export function getFallbackFields(
  batch: ProposalBatch,
  t: (key: string) => string,
): DisplayField[] {
  const opLabels: Record<string, string> = {
    create: t("aiProposals.operationCreate"),
    update: t("aiProposals.operationUpdate"),
    delete: t("aiProposals.operationDelete"),
  };

  return batch.proposals.flatMap((p) => {
    const opLabel = opLabels[p.operation] ?? p.operation;
    return [
      {
        label: t("aiProposals.operation"),
        value: `${opLabel} ${p.entityType}`,
      },
      ...payloadToFields(p.payload ?? {}),
    ];
  });
}

export function getBatchPrimaryEntityType(batch: ProposalBatch): string | null {
  if (batch.proposals.length === 0) return null;
  const hasPreOrder = batch.proposals.some((p) => p.entityType === "pre_order");
  if (hasPreOrder) return "pre_order";
  return batch.proposals[0].entityType;
}
